import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ReactLoader from 'react-loaders'
import classNames from 'classnames'
import _ from 'lodash'

import { createCommunityAccessEntry } from '../../../actions/accessEntryActions'
import { setCode, setError, refreshPermittedAt } from '../../../reducers/landingAccessReducer'
import colors from '../../../assets/styles/globals.scss'
import Logo from '../../../assets/images/logos/full-brand-logo-dark.svg'
import CodeInput from '../../common/form/CodeInput'

import classes from './CodeSection.module.scss'
import FieldError from '../../common/FieldError'
import { refreshCurrentUser } from '../../../actions/userActions'

const CodeSection = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const user = useSelector(state => state.user.current_user)
  const { description, code, community, error, isLoading } = useSelector(state => state.landingAccess)
  const [attempts, setAttempts] = useState(0)
  const [disabled, setDisabled] = useState(false)

  useEffect(() => {
    if (code.length === 4 && !isLoading) {
      dispatch(createCommunityAccessEntry({ communityId: community.id, accessEntryData: { code } })).unwrap()
        .then(() => {
          if (_.isEmpty(user)) {
            localStorage.setItem('guestAccessCode', code)
            dispatch(refreshPermittedAt())
          } else {
            dispatch(refreshCurrentUser(user.id))
              .then(() => dispatch(refreshPermittedAt()))
          }
        })
        .catch(() => setAttempts(prev => prev + 1))
    }
  }, [code, isLoading, community.id, user, dispatch])

  useEffect(() => {
    if (attempts === 10) {
      setDisabled(true)
      // We use the same rate limiting on the backend API (through WAF)
      dispatch(setError(t('programs.accessCode.tooManyAttempts')))

      setTimeout(() => {
        setAttempts(0)
        dispatch(setError(''))
        setDisabled(false)
      }, 60000)
    }
  }, [attempts, dispatch, t])

  return (
    <div className={classNames(classes.codeSection, 'h-100 d-flex flex-column')}>
      <a href='https://operation36.golf' target='_blank' className='mb-3'>
        <img src={Logo} className='mb-2 mb-lg-4' alt={t('programs.accessCode.logoAlt')} width={180} />
      </a>

      <h1 className={classNames(classes.title, 'my-4 my-lg-5')}>{t('programs.accessCode.welcome', { name: community.name })}</h1>

      <h5 className={classNames(classes.subTitle, 'mt-4')}>{t('programs.accessCode.joinWithAccessCode')}</h5>

      <p className='mt-1 mb-5'>{description}</p>

      <div className='mt-5 pb-1 pb-lg-3'>
        <label htmlFor='accessCode' className='d-flex justify-content-between align-items-center'>
          {t('programs.accessCode.enterCode')}
        </label>

        <CodeInput
          name='accessCode'
          length={4}
          value={code}
          disabled={disabled || isLoading}
          error={error ? ' ' : ''}
          onChange={(value) => dispatch(setCode(value.trim()))}
        />

        {isLoading && (
          <div className={classNames(classes.verifyingCode, 'mt-2 d-flex')}>
            <ReactLoader type='ball-pulse' color={colors.lightNavy} style={{ transform: 'scale(0.75)' }} />
          </div>
        )}

        <FieldError message={error} />
      </div>
    </div>
  )
}

export default CodeSection
